import { ISSUE_TYPE } from '../screen/GenerationPanel/Generationpanel.types';
import { JIRA_EPIC_KEY, NO_EPIC_KEY } from './Constants';

export type Issue = {
  summary: string;
  description: string;
};

export type Story = Issue & {
  id: string;
  epic_id?: string;
  isEpicChanged?: boolean;
  isNew?: boolean;
};

export type Task = Issue & {
  id: string;
  epic_id?: string;
  story_id?: string;
  isEpicChanged?: boolean;
  isStoryChanged?: boolean;
};

export type Epic = Issue & {
  id: string;
  key?: string;
  isNew?: boolean;
  jiraId?: string;
};

export type EpicsMap = {
  [key: string]: Epic;
};

export type StoriesMap = {
  [key: string]: Story;
};

export type TasksMap = {
  [key: string]: Task;
};

export type JiraEpicCollection = {
  jiraEpics: EpicsMap;
};

export type EpicCollection = {
  epics: EpicsMap;
};

export type StoryCollection = {
  stories: StoriesMap;
};

export type TaskCollection = {
  tasks: TasksMap;
};

export type Note = { 
  issueType: ISSUE_TYPE; 
  userNotes: string; 
  timestamp: Date;
  // Per-request generation counts (snapshot of what was generated for this specific request)
  generatedEpicsCount?: number;
  generatedStoriesCount?: number;
  modelName?: string;
  workItemVolume?: WorkItemVolume;
  scrollTargetId?: string;
  // Animation control flags
  isPending?: boolean;  // True while API call is in progress
  isFailed?: boolean;   // True when generation failed
  isNew?: boolean;      // True for newly added messages that should animate
};

export type WorkItemVolume = 'high' | 'medium' | 'low';

export type EpicWithStoryCollection = EpicCollection & StoryCollection;

export type IssueResponse = {
  s: string;
  d: string;
};

export type IssueResponseMap = {
  [key: string]: IssueResponse;
};
export type EpicsResponse = {
  epics: IssueResponseMap;
};
export type StoriesResponse = {
  stories: IssueResponseMap;
};

export type EpicAndStoryResponse = IssueResponse & StoriesResponse;

export type EpicsWithStoriesResponse = {
  epics: {
    [key: string]: EpicAndStoryResponse;
  };
};

export type EpicAndTaskResponse = IssueResponse & {
  tasks: IssueResponseMap;
};

export type EpicsWithTasksResponse = {
  epics: {
    [key: string]: EpicAndTaskResponse;
  };
};

export type StoryAndTaskResponse = IssueResponse & {
  tasks: IssueResponseMap;
};

export type StoriesAndTasksResponse = {
  stories: {
    [key: string]: StoryAndTaskResponse;
  };
};

export type SingleEpicWithTasksResponse = {
  epic: IssueResponse;
  tasks: IssueResponseMap;
};

export type SingleStoryWithTasksResponse = {
  story: IssueResponse;
  tasks: IssueResponseMap;
};

export type ServerError = { message: string };

export type JiraProject = {
  expand: string;
  self: string;
  id: string;
  key: string;
  name: string;
  avatarUrls: { [key: string]: unknown };
  projectTypeKey: string;
  simplified: boolean;
  style: string;
  isPrivate: boolean;
  properties: { [key: string]: unknown };
  entityId: string;
  uuid: string;
};

export type JiraIssue = {
  avatarId: number;
  description: string;
  hierarchyLevel: number;
  iconUrl: string;
  id: string;
  name: string;
  self: string;
  subtask: boolean;
};

export type CreatedIssue = {
  id: string;
  key: string;
  self: string;
};

export type JiraError = {
  status: number | string;
  elementErrors: {
    errorMessages: Array<unknown>;
    errors: unknown;
  };
  failedElementNumber: number;
};

export type CreateBulkIssuesResponse = {
  issues: CreatedIssue[];
  errors: JiraError[];
};

export interface CreateBulkEpicsResponse extends CreateBulkIssuesResponse {
  items: Epic[];
}

export interface CreateBulkStoriesResponse extends CreateBulkIssuesResponse {
  items: Story[];
}

interface createEpicAndStorySuccess {
  id: string;
  jiraId: string;
}
export interface CreateEpicAndStoriesResponse {
  success: createEpicAndStorySuccess[];
  failed: string[];
  failedReason: { [key: string]: number };
}

export interface PublishJiraResponse {
  epics: CreateEpicAndStoriesResponse;
  stories: CreateEpicAndStoriesResponse;
}

export type EpicToCreatedJiraEpicIdMap = { [key: string]: string };

export enum ConfirmActionType {
  YES,
  NO
}

export type GENERIC_ERROR_STATUS = 'DEFAULT';

export enum APP_EVENTS {
  PUBLISH_TO_JIRA = 'PUBLISH_TO_JIRA',
  SCROLL_TO_TOP = 'SCROLL_TO_TOP',
  SCROLL_TO_BOTTOM = 'SCROLL_TO_BOTTOM',
  GENERATION_SUMMARY_SELECTED = 'GENERATION_SUMMARY_SELECTED'
}

export type EpicToStoryMap = {
  [key: string]: string[];
  [NO_EPIC_KEY]: string[];
  [JIRA_EPIC_KEY]: string[];
};

export enum MessageType {
  SUCCESS,
  WARNING,
  ERROR
}

export enum PromiseStatus {
  SUCCESS = 'fulfilled',
  FAILED = 'rejected'
}
